import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import catalog from './catalog';
import newRow from './newRow';
import rebuildRow from './rebuildRow';
import { cloneDeep, intersection, remove } from 'lodash-es';
import router from '../router';

axios.defaults.baseURL = '/api/matrix/';
axios.defaults.withCredentials = true;

const state = {
	status: null,
	authorized: null,
	table: {
		schema: null,
		picklists: null,
		data: null
	},
	computed: {},
	filters: {},
	sortOrder: null,
	preFilters: []
};

const getters = {
	status(state) {
		return state.status;
	},
	authorized(state) {
		return state.authorized;
	},
	table(state) {
		return state.table;
	},
	computed(state) {
		return state.computed;
	},
	filters(state) {
		return state.filters;
	},
	preFilters(state) {
		return state.preFilters;
	},
	sortable(state) {
		return !state.sortOrder;
	},
	results(state) {
		let filters = Object.entries(state.filters);
		if (filters.length === 0) return state.computed.rows;

		let selections = intersection(
			...filters.reduce((_selections, filter) => {
				let groups = Object.entries(filter[1]);
				_selections.push([...new Set(groups.map(group => group[1]).flat())]);
				return _selections;
			}, [])
		);

		selections.sort((a, b) => {
			let aArray = a.split('-')
			let bArray = b.split('-')
			if(aArray[0] < bArray[0]) { return -1 }
			if(aArray[0] > bArray[0]) { return 1 }

			if(aArray[1] < bArray[1]) { return -1 }
			if(aArray[1] > bArray[1]) { return 1 }

			if(aArray[2] < bArray[2]) { return -1 }
			if(aArray[2] > bArray[2]) { return 1 }

			return 0
		})

		return selections.reduce((_results, selection) => {
			_results[selection] = state.computed.rows[selection];
			return _results;
		}, {});
	}
};

const mutations = {
	loadSchema(state, payload) {
		state.table.schema = payload;
		if (payload.sortOrder && payload.sortOrder.length) {
			state.sortOrder = payload.sortOrder.reduce((_sortIndex, sort) => {
				let index = payload.columns.indexOf(
					payload.columns.find(col => col.columnKey === sort)
				);
				_sortIndex.push(index);
				return _sortIndex;
			}, []).reverse();
		}
	},
	loadPicklists(state, payload) {
		state.table.picklists = payload;
	},
	loadData(state, payload) {
		state.table.data = payload;
	},
	loadAuth(state, payload) {
		state.authorized = payload;
	},
	loadBuild(state, payload) {
		state.computed = payload;
	},
	loadStatus(state, payload) {
		state.status = payload;
	},
	loadPreFilters(state, payload) {
		state.preFilters = payload;
	},
	loadFilters(state, payload) {
		let filters = cloneDeep(state.filters);
		let group, filter;

		if (!filters[payload.id]) filters[payload.id] = {};
		group = filters[payload.id];

		if (!group[payload.tag]) group[payload.tag] = [];
		filter = group[payload.tag];

		payload.data.forEach(item => {
			if (filter.includes(item)) {
				remove(filter, n => n === item);
			} else {
				filter.push(item);
			}
		});

		if (filter.length === 0) delete group[payload.tag];
		if (Object.keys(group).length === 0) delete filters[payload.id];
		state.filters = filters;

		let query = Object.entries(filters);
		query = query.map(group => {
			let filter = Object.entries(group[1]).reduce((_filter, item) => {
				_filter.push(item[0]);
				return _filter;
			}, []);
			return [group[0], filter.join('||')];
		});
		query = Object.fromEntries(query);
		router.replace({ query });
	},
	clearFilters(state, payload) {
		state.filters = {};
		router.replace({ query: null });
	},
	newRow(state, payload) {
		let rows = Object.entries(state.computed.rows);
		let keys = Object.keys(state.computed.rows);
		let index = keys.indexOf(payload.insertAfter) + 1;		
		
		rows.splice(index, 0, payload.data);

		//if this is a presorted table
		if (state.sortOrder) {
			state.sortOrder.forEach(sort => {
				rows.sort((a, b) => {
					let aVal = a[1][sort].key ? a[1][sort].key.join(' ') : a[1][sort].value;
					let bVal = b[1][sort].key ? b[1][sort].key.join(' ') : b[1][sort].value;
					return aVal.localeCompare(bVal);
				});
			});
		}
				
		state.computed.rows = Object.fromEntries(rows);
	},
	rebuildRow(state, payload) {
		state.computed.rows[payload[0]] = payload[1];
	},
	updateRows(state, payload) {
		state.computed.rows = payload;
	},
	cleanEdits(state) {
		let rows = Object.entries(state.computed.rows)
		rows.forEach(row => {
			row[1].forEach(cell => {
				delete cell._added
				delete cell._edited
			})
		})
	}
};

const actions = {
	getSchema({ commit }, id) {
		return new Promise(async resolve => {
			const response = await axios
				.get(`schema/${id}`)
				.then(res => (commit('loadSchema', res.data), true))
				.catch(err => (console.warn(err), false));
			resolve(response);
		});
	},
	getPicklists({ commit }, id) {
		return new Promise(async resolve => {
			const response = await axios
				.get(`picklists/${id}`)
				.then(res => (commit('loadPicklists', res.data), true))
				.catch(err => (console.warn(err), false));
			resolve(response);
		});
	},
	getData({ commit }, id) {
		return new Promise(async resolve => {
			const response = await axios
				.get(`data/${id}`)
				.then(res => (commit('loadData', res.data), true))
				.catch(err => (console.warn(err), false));
			resolve(response);
		});
	},
	getAuth({ commit }, edit) {
		return new Promise(async resolve => {
			commit('loadAuth', edit);
			resolve(true);
		});
	},
	build({ commit, state }, id) {
		return new Promise(async resolve => {
			await catalog(state.table, state.sortOrder).then(res => {
				commit('loadBuild', res);
				resolve(true);
			});
		});
	},
	preFilter({ commit }) {
		return new Promise(resolve => {
			let query = Object.entries(router.currentRoute.query);
			let preFilters = {};
			query.forEach(group => {
				preFilters[group[0]] = group[1].split('||');
			});
			commit('loadPreFilters', preFilters);
			resolve(true);
		});
	},
	initialize({ dispatch, commit }, params) {
		return new Promise(async resolve => {
			const [schema, picklists, data, auth] = await Promise.all([
				dispatch('getSchema', params.id),
				dispatch('getPicklists', params.id),
				dispatch('getData', params.id),
				dispatch('getAuth', params.edit)
			]);

			if (schema && picklists && data && auth) {
				await dispatch('build', params.id);
				if (Object.keys(router.currentRoute.query).length) {
					await dispatch('preFilter');
				}
				setTimeout(() => {
					commit('loadStatus', true);
				}, 1000);
			} else {
				setTimeout(() => {
					commit('loadStatus', false);
				}, 500);
			}
			resolve(true);
		});
	},
	async newBuild({ commit, state }, payload) {
		debugger
		let row = await newRow(payload.data, state.table);
		debugger
		commit('newRow', { data: row, insertAfter: payload.insertAfter });
	},
	async rebuild({ commit, state }, payload) {
		let row = await rebuildRow(payload, state.table);
		commit('rebuildRow', row);
	},
	save({ state, commit }, id) {
		return new Promise(async resolve => {
			commit('loadStatus', null);
			let payload = { columnEnumeration: state.table.data.columnEnumeration };
			let data = Object.entries(cloneDeep(state.computed.rows)).reduce((_data, row) => {
				let cells = row[1];
				let _row = cells.reduce((_row, cell) => {
					let _cell = {};
					if (cell._editorType === 'picklist') _cell.key = cell.key;
					else if (cell._editorType === 'bool') _cell.bool = cell.bool;
					else {
						_cell.value = cell.value;
						_cell.filterTag = cell.filterTag;
					}
					_row.push(_cell);
					return _row;
				}, []);
				_data.push(_row);
				return _data;
			}, []);

			payload.data = data;

			let response = await axios
				.put(`data/${id}`, payload)
				.then(res => true)
				.catch(err => false);

			commit('loadStatus', response);
			if (response) {
				commit('cleanEdits', response);
			}
			resolve(response);
		});
	}
};

Vue.use(Vuex);
export default new Vuex.Store({
	state,
	getters,
	mutations,
	actions
});
